import { Action, Reducer } from 'redux';
import {
  GotoHistoryStackEntryAction,
  HistoryStackAction,
  PushHistoryStackEntryAction,
  ReplaceHistoryStackEntryAction,
} from '../../actions';
import { HistoryStack, HistoryStackLastAction } from '../../state';
import produce, { Draft } from 'immer';
import { clamp } from 'lodash';
import random from '@robotsnacks/random-string';

/**
 * Initial history stack state that has an empty entries array.
 */
const initialState: HistoryStack<any> = {
  entries: [],
  index: -1,
  lastAction: null,
};

/**
 * Creates and returns a new history stack reducer function.
 * @param actionTypePrefix Action type prefix to watch for when receiving
 * actions and reducing the next state.
 */
export const createHistoryStackReducer = <
  N extends string,
  A extends Action = Action
>(
  actionTypePrefix: string,
  init: HistoryStack<N> = initialState,
): Reducer<HistoryStack<N>, A> => {
  // Internal helper to create actions that are prefixed.
  const makeActionType = (actionType: string): string =>
    actionTypePrefix + actionType;

  // Create the set of prefixed actions to watch for.
  const GOTO = makeActionType('GOTO');
  const POP = makeActionType('POP');
  const PUSH = makeActionType('PUSH');
  const REPLACE = makeActionType('REPLACE');

  // Return the created reducer function.
  return (
    state: HistoryStack<N> = init,
    action: HistoryStackAction,
  ): HistoryStack<N> =>
    produce(state, (draft): void => {
      // Handle a PUSH action. This will add a new stack entry at the top of
      // the stack and move the current index to point to the new entry.
      if (action.type === PUSH) {
        const { name, state } = action as PushHistoryStackEntryAction;
        draft.entries.push({ name: name as Draft<N>, state, key: random(6) });
        draft.index = draft.entries.length - 1;
        draft.lastAction = HistoryStackLastAction.Push;
      }

      // Pop the top entry off the stack and clamp the current index.
      else if (action.type === POP) {
        draft.entries.pop();
        draft.index = clamp(draft.index, -1, draft.entries.length - 1);
        draft.lastAction = HistoryStackLastAction.Pop;
      }

      // Moves the current index to some entry within the stack. The action's
      // index is clamped to the stack's range.
      else if (action.type === GOTO) {
        const { index } = action as GotoHistoryStackEntryAction;
        const lastIndex = draft.index;
        draft.index = clamp(index, 0, draft.entries.length - 1);
        draft.lastAction =
          draft.index > lastIndex
            ? HistoryStackLastAction.Forward
            : draft.index < lastIndex
            ? HistoryStackLastAction.Backward
            : draft.lastAction;
      }

      // Replaces some entry within the stack.
      else if (action.type === REPLACE) {
        const {
          name,
          index = draft.index,
          state,
        } = action as ReplaceHistoryStackEntryAction;
        if (draft.entries[index]) {
          draft.entries[index] = {
            name: name as Draft<N>,
            state,
            key: random(6),
          };
        }
        draft.lastAction = HistoryStackLastAction.Replace;
      }
    });
};
